import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { CodyIcon } from '../components/CodyLogo';
import { ArrowLeft, Plus, Tag, Trash2, Pencil, Copy, Check } from 'lucide-react';

export default function OpsCoupons() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  // Create form
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [description, setDescription] = useState('');
  const [applicablePlans, setApplicablePlans] = useState('all');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchCoupons();
  }, [user]);

  const fetchCoupons = async () => {
    setLoading(true);
    try { const c = await api.getAdminCoupons(); setCoupons(c); }
    catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!code.trim() || !discountValue) { toast.error('Code and value required'); return; }
    setCreating(true);
    try {
      await api.createCoupon({
        code: code.trim(),
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions) : null,
        expires_at: expiresAt || null,
        applicable_plans: applicablePlans === 'all' ? null : [applicablePlans],
        description,
      });
      toast.success('Coupon created!');
      setShowCreate(false);
      resetForm();
      fetchCoupons();
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const handleToggle = async (couponId, currentActive) => {
    try { await api.updateCoupon(couponId, { is_active: !currentActive }); fetchCoupons(); }
    catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (couponId) => {
    try { await api.deleteCoupon(couponId); toast.success('Coupon deleted'); fetchCoupons(); }
    catch (e) { toast.error(e.message); }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    setCopiedCode(c);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const resetForm = () => {
    setCode(''); setDiscountType('percentage'); setDiscountValue('');
    setMaxRedemptions(''); setExpiresAt(''); setDescription(''); setApplicablePlans('all');
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'CODY';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    setCode(result);
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="bg-white border-b border-[#E6E8EE] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-xs text-[#5B667A]" onClick={() => navigate('/ops')}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Ops</Button>
          </div>
          <div className="flex items-center gap-2"><CodyIcon size={28} /><span className="text-xs text-[#9ca3af]">Coupon Management</span></div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold" style={{ fontFamily: "'Space Grotesk'" }}>Coupons</h1>
            <p className="text-sm text-[#5B667A]">{coupons.length} total · {coupons.filter(c => c.is_active).length} active</p>
          </div>
          <Button className="h-10 px-5 rounded-lg text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }}
            onClick={() => { resetForm(); setShowCreate(true); }}>
            <Plus className="w-4 h-4 mr-1.5" /> Create Coupon
          </Button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white mb-6">
            <h3 className="text-base font-bold mb-5" style={{ fontFamily: "'Space Grotesk'" }}>New Coupon</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Coupon Code</label>
                <div className="flex gap-2">
                  <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SAVE20" className="h-10 text-sm font-mono uppercase flex-1" />
                  <Button variant="outline" size="sm" className="h-10 text-xs" onClick={generateCode}>Generate</Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Description</label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="20% off for early adopters" className="h-10 text-sm" />
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Type</label>
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="fixed">Fixed ($)</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Value</label>
                <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === 'percentage' ? '20' : '10.00'} className="h-10 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Max Uses</label>
                <Input type="number" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} placeholder="Unlimited" className="h-10 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Expires</label>
                <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="h-10 text-sm" />
              </div>
            </div>
            <div className="mb-5">
              <label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Applicable Plans</label>
              <Select value={applicablePlans} onValueChange={setApplicablePlans}>
                <SelectTrigger className="h-10 text-sm w-48"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Plans</SelectItem><SelectItem value="pro">Growth Only</SelectItem><SelectItem value="agency">Agency Only</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button className="h-10 px-6 text-sm font-semibold rounded-lg" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }} onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Coupon'}
              </Button>
              <Button variant="ghost" className="h-10 text-sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Coupon List */}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
        ) : coupons.length === 0 ? (
          <Card className="p-12 rounded-xl border-[#E6E8EE] bg-white text-center">
            <Tag className="w-10 h-10 text-[#d1d5db] mx-auto mb-3" />
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>No coupons yet</h3>
            <p className="text-sm text-[#5B667A]">Create your first coupon to offer discounts.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {coupons.map(c => (
              <Card key={c._id} className={`p-5 rounded-xl border-[#E6E8EE] bg-white ${!c.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#FFF2EA]">
                      <Tag className="w-5 h-5 text-[#FF4500]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold font-mono text-[#0B0F17]">{c.code}</span>
                        <button onClick={() => copyCode(c.code)} className="text-[#9ca3af] hover:text-[#FF4500]">
                          {copiedCode === c.code ? <Check className="w-3 h-3 text-[#16A34A]" /> : <Copy className="w-3 h-3" />}
                        </button>
                        <Badge className={`text-[9px] ${c.is_active ? 'bg-[#E9F9EF] text-[#16A34A]' : 'bg-[#F3F4F6] text-[#5B667A]'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-xs text-[#5B667A]">
                        {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `$${c.discount_value} off`}
                        {c.description ? ` — ${c.description}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-xs text-[#5B667A]">
                    <div className="text-center">
                      <p className="font-bold text-[#0B0F17] tabular-nums">{c.redemptions || 0}{c.max_redemptions ? `/${c.max_redemptions}` : ''}</p>
                      <p className="text-[9px]">Used</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px]">{c.applicable_plans ? c.applicable_plans.join(', ') : 'All plans'}</p>
                      <p className="text-[9px]">Plans</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px]">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</p>
                      <p className="text-[9px]">Expires</p>
                    </div>
                    <Switch checked={c.is_active} onCheckedChange={() => handleToggle(c._id, c.is_active)} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9ca3af] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {c.code}?</AlertDialogTitle><AlertDialogDescription>This coupon will be permanently deleted.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => handleDelete(c._id)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
